import styled, { css } from 'styled-components';
import { theme, cssVar } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';

export const TanStackGridWrap = styled.div`
  position: relative;
  width: 100%;
  min-height: 0;
  overflow: auto;
  border: 1px solid ${cssVar('color-grid-border', theme.colors.grid.border)};
  border-radius: ${theme.borderRadius.md};
  background-color: ${cssVar('color-bg-white', theme.colors.white)};
  outline: none;

  &:focus {
    outline: none;
  }

  ${hoverExpandScrollbarCss}
`;

/** 고정 헤더 영역 */
export const HiGridHeaderScrollArea = styled.div`
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* 상단 파란 accent 라인 제거 — 외곽은 HiGrid 인라인 border만 사용 */
  border-top: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TanStackTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: var(--erp-font-size-sm, ${theme.typography.fontSize.sm});
`;

export const TanStackTh = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  background-color: ${cssVar('color-grid-header-bg', theme.colors.grid.headerBg)};
  color: ${cssVar('color-grid-header-text', theme.colors.grid.headerText)};
  border-bottom: 1px solid ${cssVar('color-grid-border', theme.colors.grid.border)};
  white-space: nowrap;
  &:last-child {
    border-right: none;
  }
  /* HiGrid headerActive — 인라인 스타일과 함께 사용, 기본 회색 덮어쓰기 방지 */
  &[data-header-active='Y'] {
    background-color: ${cssVar('color-grid-header-active-bg', theme.colors.grid.headerActiveBg)} !important;
    color: ${cssVar('color-grid-header-active-fg', theme.colors.grid.headerActiveFg)} !important;
    border-bottom-color: ${cssVar('color-grid-header-active-bg', theme.colors.grid.headerActiveBg)};
  }
`;

export const ROW_HEIGHT = 28;

/** 행 높이는 HiGrid inline style(rowHeight)로 지정 — 스택 셀(52px)과 기본(28px) 혼용 */
export const TanStackTr = styled.tr`
  box-sizing: border-box;
  &:hover:not(.row-selected) {
    background-color: ${cssVar('color-grid-row-hover', theme.colors.grid.rowHover)} !important;
  }
  &.row-selected {
    background-color: ${cssVar('color-grid-row-selected', theme.colors.grid.rowSelected)} !important;
  }
  &.row-selected td:not(.cell-selected) {
    background-color: ${cssVar('color-grid-row-selected', theme.colors.grid.rowSelected)} !important;
  }
`;

export const TanStackTd = styled.td`
  position: relative;
  /* 고정 28px 제거 — 스택/일반 행 높이 불일치로 선택·포커스 영역이 틀어지던 원인 */
  box-sizing: border-box;
  padding: 0 10px;
  vertical-align: middle;
  border-bottom: 1px solid ${cssVar('color-grid-border', theme.colors.grid.border)};
  outline: none;
  user-select: none;
  overflow: hidden;
  line-height: 1.4;
  font-family: var(--erp-grid-font-family, ${theme.typography.gridFontFamily});
  font-weight: 400;
  color: ${cssVar('color-text', theme.colors.text.primary)};
  tr:hover:not(.row-selected) &:not(.cell-selected) {
    background-color: ${cssVar('color-grid-row-hover', theme.colors.grid.rowHover)} !important;
  }
  /* 블럭 선택 — 선택 행보다 우선 (첫 행이 블럭에서 빠지듯 보이던 문제) */
  &.cell-selected {
    background-color: ${cssVar('color-grid-cell-selected', theme.colors.grid.cellSelected)} !important;
    z-index: 1;
    /* 블럭 외곽 가이드 — --sel-b* 는 가장자리 셀에만 2px */
    box-shadow:
      inset 0 var(--sel-bt, 0px) 0 0
        ${cssVar('color-grid-cell-block-border', theme.colors.grid.cellBlockBorder)},
      inset 0 calc(-1 * var(--sel-bb, 0px)) 0 0
        ${cssVar('color-grid-cell-block-border', theme.colors.grid.cellBlockBorder)},
      inset var(--sel-bl, 0px) 0 0 0
        ${cssVar('color-grid-cell-block-border', theme.colors.grid.cellBlockBorder)},
      inset calc(-1 * var(--sel-br, 0px)) 0 0 0
        ${cssVar('color-grid-cell-block-border', theme.colors.grid.cellBlockBorder)};
  }
  /* 포커스는 outline — 블럭 테두리(box-shadow)와 겹치지 않음 */
  &.cell-focused {
    outline: 2px solid ${cssVar('color-grid-cell-focused', theme.colors.grid.cellFocused)};
    outline-offset: -2px;
    z-index: 2;
  }
  &.cell-copied {
    border: 1px dashed ${cssVar('color-primary', theme.colors.primary)} !important;
    z-index: 1;
  }
`;

/////////////////////////////////
// Editor Styles
/////////////////////////////////

export const EditorCellWrap = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  & > input:not([type='checkbox']),
  & > select,
  & > [data-cell-editor-shell] {
    flex: 1;
    min-width: 0;
    align-self: stretch;
    height: 100%;
  }
`;

const cellEditorCommon = css`
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 4px 8px;
  border: 1px solid ${cssVar('color-primary', theme.colors.primary)};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${cssVar('color-bg-white', theme.colors.white)};
  font-family: var(--erp-grid-font-family, ${theme.typography.gridFontFamily});
  font-weight: 400;
  font-size: var(--erp-font-size-sm, ${theme.typography.fontSize.sm});
  box-sizing: border-box;
  line-height: 1.4;
`;

export const TextEditorInput = styled.input`
  ${cellEditorCommon}
`;

export const NumberField = styled.input`
  ${cellEditorCommon}
`;

export const DateField = styled.input`
  ${cellEditorCommon}
`;

export const ComboboxField = styled.select`
  ${cellEditorCommon}
`;

export const CheckboxField = styled.input`
  ${cellEditorCommon}
`;

export const RadioField = styled.input`
  ${cellEditorCommon}
`;
